// CoursePreviewModal — read-only course outline preview for org admins.
// Lets an organisation see what a Master Catalog or Community Library
// course actually contains (modules, lessons, objectives, concept counts)
// BEFORE copying it into their hub. Rendered inside CreateCourseModal and
// EducationAdminPage, so admins inspect outlines without leaving their picker.
import React, { useEffect, useState } from 'react'
import { Loader2, X, BookOpen, Layers, CheckCircle } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useFacilityBranding } from '@/contexts/SubdomainBrandingContext'
import { renderFormattedText, shouldShowAge } from '@/utils/textClean'
import { classifyStream, type CatalogStream } from '@/utils/catalogStreams'

const CARD = '#0f172a'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#f8fafc'
const MU = 'rgba(255,255,255,0.65)'
const MU2 = 'rgba(255,255,255,0.40)'
const GR = '#4ade80'
const GOLD = 'var(--org-accent, #3b82f6)'
const SAGE = '#4a7c5e'

export interface PreviewCourse {
    id: string
    name: string
    source: 'master' | 'community'
    description?: string | null
    subject_group?: string | null
    curriculum_track?: string | null
    target_age?: number | null
}

const pill = (label: string, color: string): React.CSSProperties => ({
    fontSize: 9.5, padding: '3px 9px', borderRadius: 20, fontWeight: 800,
    background: `${color}18`, color, flexShrink: 0, textTransform: 'uppercase',
    letterSpacing: '0.05em', border: `1px solid ${color}30`,
})

export default function CoursePreviewModal({ course, onClose }: {
    course: PreviewCourse
    onClose: () => void
}) {
    const [detail, setDetail] = useState<any | null>(null)
    const [loading, setLoading] = useState(true)
    const branding = useFacilityBranding()
    const showAge = shouldShowAge(branding?.org_type)

    useEffect(() => {
        let alive = true
        setLoading(true); setDetail(null)
        // Ensure module_plan is fetched for both master classes AND community courses
        const req = course.source === 'community'
            ? supabase.from('community_course_library' as any)
                .select('name, description, subject_group, target_age, module_count, clone_count, author_name, origin_facility_name, origin, module_plan')
                .eq('id', course.id).maybeSingle()
            : supabase.from('classes')
                .select('name, description, subject_group, curriculum_track, target_age, lesson_count, module_plan')
                .eq('id', course.id).maybeSingle()

        req.then(({ data }: any) => {
            if (!alive) return
            setDetail(data ?? {})
            setLoading(false)
        })
        return () => { alive = false }
    }, [course.id, course.source])

    const d: any = detail || {}
    const plan: any[] = Array.isArray(d.module_plan) ? d.module_plan : []
    const lessonTotal = plan.reduce((n: number, m: any) =>
        n + (Array.isArray(m.lessons) ? m.lessons.length : 0), 0)
    const totalConcepts = plan.reduce((s: number, m: any) =>
        s + (Array.isArray(m.lessons) ? m.lessons.reduce((x: number, l: any) => x + (Array.isArray(l.core_concepts) ? l.core_concepts.length : 0), 0) : 0), 0)

    const streamKey = classifyStream({ ...course, ...d })
    const streamMap: Record<CatalogStream, { label: string; color: string }> = {
        standard: { label: 'Standard', color: '#38bdf8' },
        elite: { label: 'Elite', color: '#f59e0b' },
        university: { label: 'University', color: '#a78bfa' },
        community: { label: d.origin === 'master' ? 'Staff pick' : 'Community', color: SAGE },
        church: { label: 'Faith', color: '#d97706' },
    }
    const streamMeta = streamMap[streamKey] || streamMap.standard
    const streamLabel = streamMeta.label
    const streamColor = streamMeta.color

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 400,
            background: 'rgba(6,6,12,0.85)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px 12px',
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                background: CARD, border: `1px solid ${BR}`, borderRadius: 20,
                width: '100%', maxWidth: 580, maxHeight: '88dvh', ['--org-accent' as any]: ((branding as any)?.primary_color || (branding as any)?.secondary_color || (branding as any)?.accent_color || '#3b82f6'), overflowY: 'auto',
                padding: '24px 22px', boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <span style={pill(streamLabel, streamColor)}>{streamLabel}</span>
                            {d.subject_group && <span style={pill(d.subject_group, GOLD)}>{d.subject_group}</span>}
                        </div>
                        <h3 style={{ fontSize: 19, fontWeight: 800, color: TX, margin: 0, lineHeight: 1.35 }}>
                            {renderFormattedText(d.name || course.name)}
                        </h3>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'rgba(255,255,255,0.06)', border: `1px solid ${BR}`,
                        borderRadius: '50%', cursor: 'pointer', color: MU, padding: 6, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <X size={18} />
                    </button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '14px 0 16px', alignItems: 'center' }}>
                    {showAge && d.target_age && (
                        <span style={{ fontSize: 11, color: MU, background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: 6 }}>
                            Age {d.target_age}+
                        </span>
                    )}
                    {plan.length > 0 && (
                        <span style={{ fontSize: 11, color: GOLD, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Layers size={13} /> {plan.length} modules · {lessonTotal} lessons {totalConcepts > 0 ? `· ${totalConcepts} concepts` : ''}
                        </span>
                    )}
                    {course.source === 'community' && d.author_name && (
                        <span style={{ fontSize: 11, color: MU }}>
                            by {d.origin === 'master' ? 'Still Waters Academy' : d.author_name}{d.origin_facility_name && d.origin !== 'master' ? ` · ${d.origin_facility_name}` : ''}
                        </span>
                    )}
                </div>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: MU2, fontSize: 13 }}>
                        <Loader2 size={22} className="animate-spin" style={{ margin: '0 auto 10px', display: 'block', color: GOLD }} />
                        Loading syllabus outline…
                    </div>
                ) : (
                    <>
                        {d.description && (
                            <div style={{ fontSize: 13, color: MU, lineHeight: 1.6, margin: '0 0 18px', background: 'rgba(255,255,255,0.03)', padding: '12px 14px', borderRadius: 12, border: `1px solid ${BR}` }}>
                                {renderFormattedText(d.description)}
                            </div>
                        )}

                        {plan.length > 0 ? (
                            <div style={{ border: `1px solid ${BR}`, borderRadius: 14, overflow: 'hidden', background: 'rgba(0,0,0,0.2)' }}>
                                {plan.map((m: any, i: number) => {
                                    const lessons: any[] = Array.isArray(m.lessons) ? m.lessons : []
                                    return (
                                        <div key={i} style={{ padding: '12px 14px', borderBottom: i < plan.length - 1 ? `1px solid ${BR}` : 'none' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                                                <span style={{ fontWeight: 800, color: TX, fontSize: 13.5, lineHeight: 1.4 }}>
                                                    {i + 1}. {renderFormattedText(m.title || `Module ${i + 1}`)}
                                                </span>
                                                <span style={{ fontSize: 10.5, color: MU2, flexShrink: 0 }}>
                                                    {lessons.length} lesson{lessons.length === 1 ? '' : 's'}
                                                </span>
                                            </div>
                                            {m.learning_objective && (
                                                <div style={{ margin: '5px 0 8px', fontSize: 12, color: MU, lineHeight: 1.5 }}>
                                                    <span style={{ fontWeight: 800, color: GOLD }}>Objective:</span> {renderFormattedText(m.learning_objective)}
                                                </div>
                                            )}
                                            {lessons.map((l: any, li: number) => (
                                                <div key={li} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12, color: MU, padding: '3px 0 3px 12px' }}>
                                                    <span style={{ color: SAGE, fontWeight: 800, fontSize: 11 }}>{li + 1}</span>
                                                    <span style={{ flex: 1, minWidth: 0, color: 'rgba(255,255,255,0.85)' }}>
                                                        {renderFormattedText(l.title || l.lesson_title || `Lesson ${li + 1}`)}
                                                    </span>
                                                    {Array.isArray(l.core_concepts) && l.core_concepts.length > 0 && (
                                                        <span style={{ fontSize: 10, color: MU2, flexShrink: 0 }}>
                                                            {l.core_concepts.length} concepts
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div style={{ padding: '24px 18px', textAlign: 'center', background: 'rgba(255,255,255,0.03)', borderRadius: 14, border: `1px dashed ${BR}` }}>
                                <BookOpen size={28} style={{ margin: '0 auto 10px', display: 'block', color: GOLD }} />
                                <p style={{ color: MU, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                                    {course.source === 'community' && d.module_count
                                        ? `${d.module_count} modules generated — full syllabus will sync automatically when copied to your organisation.`
                                        : 'Live AI curriculum — syllabus generates dynamically according to learner progress.'}
                                </p>
                            </div>
                        )}

                        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                            <p style={{ fontSize: 11.5, color: MU2, margin: 0, lineHeight: 1.4 }}>
                                Previewing {course.source === 'community' ? 'community library' : 'master catalog'} syllabus. Creating gives your organisation an independent, private copy.
                            </p>
                            <button onClick={onClose} style={{
                                padding: '8px 18px', borderRadius: 10, border: 'none',
                                background: 'rgba(255,255,255,0.08)', color: TX, fontWeight: 700,
                                fontSize: 12.5, cursor: 'pointer',
                            }}>
                                Close
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}